import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, flatMap, map, switchMap, tap } from 'rxjs/operators';
import * as fromShared from '../../shared';
import { DisplayMessageMinimum, ResponseStatus } from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { ContractStructureService } from '../contract-structure.service';
import { IContractStructure } from '../IContractStructure';
import * as fromCJ from './actions';

@Injectable()
export class CustomerJobEffects {
    newCJ: Array<IContractStructure> = [];

    constructor(
        private actions$: Actions,
        private service: ContractStructureService,
        private log: LoggingService
    ) {}

    LoadCustomerJobs$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromCJ.LoadCustomerJobs>(
                fromCJ.ActionTypes.LoadCustomerJobs
            ),
            tap(action =>
                this.log.debug({
                    type: 'CustomerJobEffects:LoadCustomerJobs$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getCustomerJobs(x.payload, x.filterpayload).pipe(
                    flatMap(cs => [
                        new fromCJ.SetCustomerJobs(cs),
                        new fromShared.Errors.SetErrors(
                            [
                                ...this.flattenNestedData(cs)
                                    .filter(
                                        a =>
                                            a.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(a => a.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadContractTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromCJ.LoadContractTypes>(
                fromCJ.ActionTypes.LoadContractTypes
            ),
            tap(action =>
                this.log.debug({
                    type: 'CustomerJobEffects:LoadContractTypes$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getContractTypes().pipe(
                    flatMap(exp => [new fromCJ.SetContractTypes(exp)]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            ),
            catchError((e, c) => {
                this.log.error({ e, c });
                return c;
            })
        )
    );

    LoadEntityFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromCJ.LoadEntityFilter>(
                fromCJ.ActionTypes.LoadEntityFilter
            ),
            tap(action =>
                this.log.debug({
                    type: 'UserEntryDatesEffects:LoadEntityFilter$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getEntityFilter().pipe(
                    map(exp => new fromCJ.SetEntityFilter(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            ),
            catchError((e, c) => {
                this.log.error({ e, c });
                return c;
            })
        )
    );

    LoadCustomers$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromCJ.LoadCustomers>(fromCJ.ActionTypes.LoadCustomers),
            tap(action =>
                this.log.debug({
                    type: 'UserEntryDatesEffects:LoadCustomers$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getCustomers().pipe(
                    map(exp => new fromCJ.SetCustomers(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            ),
            catchError((e, c) => {
                this.log.error({ e, c });
                return c;
            })
        )
    );
    UpdateContractStructure$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromCJ.UpdateContractStructure>(
                fromCJ.ActionTypes.UpdateContractStructure
            ),
            tap(x =>
                this.log.verbose({
                    type: 'ContractStructureEffects:UpdateContractStructure$',
                    x
                })
            ),
            switchMap(a =>
                this.service.putContractStructure([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromCJ.UpdateContractStructureSuccess(resp),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.QB_ListID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...this.flattenNestedData(resp.Items)
                                    .filter(
                                        a =>
                                            a.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(a => a.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    public flattenNestedData(
        cs: Array<IContractStructure>
    ): Array<IContractStructure> {
        const flatten = cs => {
            return cs.reduce((r, { _children, ...rest }) => {
                r.push(rest);
                if (_children) {
                    r.push(...flatten(_children));
                }
                return r;
            }, []);
        };
        return flatten(cs);
    }
}
